<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import GeneratedAssessmentTile from '../assessments/GeneratedAssessmentTile.vue'
import { useSavedPageGeneratedAssessments } from '../assessments/useSavedPageGeneratedAssessments'
import AnchorPageObjectives from '../objectives/AnchorPageObjectives.vue'
import { useAnchorPageStore } from '../anchor_page.store'
import { useQuarterlyObjectiveResources } from '../anchor-page/useQuarterlyObjectiveResources'

const props = defineProps<{
  savedPageUuid: string
}>()

const anchorPageStore = useAnchorPageStore()
const { content, accessInfo, actionPermissions, strategyMap } = storeToRefs(anchorPageStore)
const { assessmentsForLevel, levelsWithGeneratedAssessments } = useSavedPageGeneratedAssessments(() => props.savedPageUuid)

const objectives = computed(() => content.value?.objectives ?? [])
const objectiveTexts = computed(() => objectives.value.map((objective) => objective.text))
const objectiveResourceUuids = computed(() => content.value?.objective_resources)
// Vue 2 never locked a shared copy's quarterly objectives: they are the owner's text to show.
const unlockedAccessInfo = computed(() => ({ ...accessInfo.value, blur: false }))

const { resourcesByLevel, isFetching: isFetchingResources } = useQuarterlyObjectiveResources(objectiveResourceUuids)
</script>

<template>
  <!-- The owner's objective text often names the student, so it is kept out of session recordings. -->
  <div v-if="objectives.length > 0" class="fs-exclude" data-testid="quarterly-objectives">
    <h2 class="mb-6 mt-9 text-display-sm font-medium leading-tight">Quarterly Objectives</h2>
    <AnchorPageObjectives
      :objectives="objectives"
      :objective-texts="objectiveTexts"
      :items="content?.assessment_items"
      :page-uuid="content?.uuid"
      :access-info="unlockedAccessInfo"
      :print-permissions="actionPermissions.printAssessment"
      :strategies="strategyMap"
      :objective-resource-uuids="objectiveResourceUuids"
      :resources-by-level="resourcesByLevel"
      :is-fetching-resources="isFetchingResources"
      :levels-with-generated-assessments="levelsWithGeneratedAssessments"
    >
      <template #generatedAssessments="{ objective }">
        <GeneratedAssessmentTile
          v-for="assessment in assessmentsForLevel(objective.level)"
          :key="assessment.uuid"
          :assessment="assessment"
        />
      </template>
    </AnchorPageObjectives>
  </div>
</template>
